import { Box, Tooltip } from "@mui/material";
import { HtmlText } from "../../../components/HtmlText";
import { IconTypes } from "../../../components/icons.types";
import { CatalogBuilder } from "../../../features/catalogs/catalog.builder";
import { getPageItems } from "../../../features/catalogs/catalog.page";
import { CatalogPageRef } from "../../../features/catalogs/catalogs";
import { DesignItemRef } from "../../../features/design/design.types";
import { renderPageBackground, renderCatalogItem } from "./catalog.render";

type CatalogPagePreviewProps = {
  builder: CatalogBuilder;
  page: CatalogPageRef;
  scale?: number;
  isQuickPreview?: boolean;
  blockContent?: boolean;
  /** When provided, elements become clickable (used by concierge preview) */
  onItemClick?: (item: DesignItemRef, event: React.MouseEvent) => void;
  /** When provided, shows tooltip on hover describing what clicking the element will do */
  getItemTooltip?: (item: DesignItemRef) => string;
};

export const CatalogPagePreview = ({
  builder,
  page,
  scale = 1,
  blockContent,
  onItemClick,
  getItemTooltip,
}: CatalogPagePreviewProps) => {
  if (!page) return null;

  const pageItems = getPageItems(builder, page);

  return (
    <Box
      className="page-preview"
      sx={{
        height: builder.board.h,
        width: builder.board.w,
        transform: "scale(" + scale + ")",
        transformOrigin: "0 0",
      }}
    >
      {renderPageBackground(page)}
      {page.headerHeight > 0 && (
        <Box
          style={{
            height: page.headerHeight,
            backgroundColor: page.headerBkgColor,
          }}
          className="page-part page-header"
        >
          <HtmlText content={page.headerContent} />
          {/* <div
            className="unstyled-text  content-view"
            dangerouslySetInnerHTML={{
              __html: page.headerContent,
            }}
          /> */}
        </Box>
      )}
      {page.pageItem?.di.PageType === "toc" && (
        <Box
          className="page-part"
          sx={{
            height: page.pageHeight,
            display: "flex",
            justifyContent: "center",
            svg: {
              mt: page.pageHeight / 8 + "px",
              fontSize: page.pageHeight / 2,
              color: "#aaa",
            },
          }}
        >
          <IconTypes.Toc />
        </Box>
      )}
      <Box
        style={{
          height: page.pageHeight,
        }}
        className="page-part page-body"
      >
        {builder?.context.activePageId >= 0 &&
          pageItems
            .filter((i) => i.di.Type !== "page")
            .map((item) => {
              const tooltip = getItemTooltip?.(item) ?? "";
              const el = (
                <Box
                  key={item.id}
                  id={item.id}
                  style={item.style}
                  className="page-thumb-item"
                  data-product={item.di.ItemId}
                  onClick={
                    onItemClick
                      ? (e: React.MouseEvent) => {
                          e.stopPropagation();
                          onItemClick(item, e);
                        }
                      : undefined
                  }
                  sx={
                    onItemClick
                      ? {
                          cursor: "pointer",
                          "&:hover": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -1,
                            zIndex: 9999,
                          },
                        }
                      : undefined
                  }
                >
                  {renderCatalogItem(item, false, blockContent, true)}
                </Box>
              );
              return tooltip ? (
                <Tooltip
                  key={item.id}
                  title={tooltip}
                  placement="top"
                  enterDelay={300}
                  disableInteractive
                  slotProps={{
                    popper: {
                      sx: { pointerEvents: "none" },
                    },
                  }}
                >
                  {el}
                </Tooltip>
              ) : (
                el
              );
            })}
      </Box>
      {page.footerHeight > 0 && (
        <Box
          style={{
            height: page.footerHeight,
            backgroundColor: page.footerBkgColor,
          }}
          className="page-part page-footer"
        >
          <HtmlText content={page.footerContent} />
          {/* <div
            className="unstyled-text  content-view"
            dangerouslySetInnerHTML={{
              __html: page.footerContent,
            }}
          /> */}
        </Box>
      )}
    </Box>
  );
};
